import { useMemo, useState } from 'react';

import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LiveHelpRoundedIcon from '@mui/icons-material/LiveHelpRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useDomainPermissions } from '@/features/store-settings';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useAcceptChatbotTemplateDefault,
  useChatbotFaqIntents,
  useChatbotTemplateCatalog,
  useContentLanguages,
  useDeleteChatbotFaqIntent,
  usePutChatbotFaqIntent,
  usePutChatbotTemplate,
  useResetChatbotTemplate,
} from '../../hooks';
import { templateGroupLabelKey, templateLabelKeys } from '../../model/templateLabels';
import type { ChatbotFaqIntent, ChatbotTemplateCatalogItem } from '../../model/types';
import { FaqEntryDialog, LocaleSwitcher, TemplateEditorDrawer } from '../components';

type RowFilter = 'all' | 'attention' | 'yours' | 'default' | 'missing' | 'variants';

const STATUS_CHIP: Record<
  ChatbotTemplateCatalogItem['resolution'],
  {
    color: 'primary' | 'default' | 'warning' | 'info';
    variant: 'filled' | 'outlined';
    icon?: 'verified';
  }
> = {
  TENANT: { color: 'primary', variant: 'filled' },
  ACCEPTED_DEFAULT: { color: 'default', variant: 'outlined', icon: 'verified' },
  SYSTEM_DEFAULT: { color: 'info', variant: 'outlined' },
  EMPTY: { color: 'warning', variant: 'filled' },
};

function hasKnownIssue(item: ChatbotTemplateCatalogItem, knownCodes: Set<string>): boolean {
  return item.issues.some((issue) => knownCodes.has(issue.code));
}

const KNOWN_ISSUE_CODES = new Set([
  'PLACEHOLDER_NOT_SUPPLIED',
  'ACTION_MARKUP_CONFLICT',
  'DEEP_LINK_TARGET_UNKNOWN',
  'COMPOSED_MARKUP_CONFLICT',
  'EXPECTED_ACTION_MISSING',
  'KEY_RETIRED',
  'KEY_UNKNOWN',
  'ANSWER_TEMPLATE_MISSING',
  'USED_AS_FAQ_ANSWER',
  'LEGACY_FAQ_ANSWER_KEY',
]);

/** The row's one-line preview text (design §13.3's status/preview table) — also what `Search`
 * matches against, alongside the label and the key (design §13.3: "matches the label, the key and
 * the preview text — the shipped control matched the label only"). */
function previewTextOf(item: ChatbotTemplateCatalogItem): string {
  if (item.resolution === 'EMPTY') return '';
  if (item.resolution === 'SYSTEM_DEFAULT') {
    return item.template.defaultParts.find((p) => p.type === 'TEXT')?.text ?? '';
  }
  return item.template.effectiveParts.find((p) => p.type === 'TEXT')?.text ?? '';
}

function matchesFilter(item: ChatbotTemplateCatalogItem, filter: RowFilter): boolean {
  switch (filter) {
    case 'attention':
      return (
        hasKnownIssue(item, KNOWN_ISSUE_CODES) ||
        (item.resolution === 'EMPTY' && item.requirement !== 'OPTIONAL') ||
        item.family === 'UNLISTED'
      );
    case 'yours':
      return item.resolution === 'TENANT';
    case 'default':
      return item.resolution === 'ACCEPTED_DEFAULT' || item.resolution === 'SYSTEM_DEFAULT';
    case 'missing':
      return item.resolution === 'EMPTY';
    case 'variants':
      return item.channelVariants.length > 0;
    default:
      return true;
  }
}

function issueSentence(
  issue: ChatbotTemplateCatalogItem['issues'][number],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const base = (() => {
    switch (issue.code) {
      case 'PLACEHOLDER_NOT_SUPPLIED':
        return t('templates.issues.PLACEHOLDER_NOT_SUPPLIED', {
          placeholder: `{{${issue.detail}}}`,
        });
      case 'ACTION_MARKUP_CONFLICT':
        return t('templates.issues.ACTION_MARKUP_CONFLICT');
      case 'DEEP_LINK_TARGET_UNKNOWN':
        return t('templates.issues.DEEP_LINK_TARGET_UNKNOWN', { target: issue.detail });
      case 'COMPOSED_MARKUP_CONFLICT':
        return t('templates.issues.COMPOSED_MARKUP_CONFLICT', { other: issue.detail });
      case 'EXPECTED_ACTION_MISSING':
        return t('templates.issues.EXPECTED_ACTION_MISSING', { action: issue.detail });
      case 'KEY_RETIRED':
        return issue.detail
          ? t('templates.issues.KEY_RETIRED', { replacement: issue.detail })
          : t('templates.issues.KEY_RETIRED_none');
      case 'KEY_UNKNOWN':
        return t('templates.issues.KEY_UNKNOWN');
      case 'ANSWER_TEMPLATE_MISSING':
        return t('templates.issues.ANSWER_TEMPLATE_MISSING', { language: issue.detail });
      case 'USED_AS_FAQ_ANSWER':
        return t('templates.issues.USED_AS_FAQ_ANSWER', { intent: issue.detail });
      case 'LEGACY_FAQ_ANSWER_KEY':
        return t('templates.issues.LEGACY_FAQ_ANSWER_KEY');
      default:
        return issue.code;
    }
  })();
  return issue.channel
    ? t('templates.issues.onChannel', { channel: issue.channel, issue: base })
    : base;
}

/**
 * «Settings → Chatbot → Bot texts & FAQ» — the templates and FAQ editor, rebuilt on the frozen bot
 * template catalogue (templates doc §13, web-content-service adr-content-005). One request replaces
 * the deleted static `model/templateCatalog.ts` registry and its 42-query composition.
 */
export function ChatbotTemplatesPage() {
  const { t, i18n } = useTranslation(['chatbot', 'settings']);
  const { hasPermission } = usePermissions();
  const { canView, canManage } = useDomainPermissions('chatbot-templates');
  const canPublish = canManage && hasPermission('content.feature.publish');

  const [searchParams, setSearchParams] = useSearchParams();
  const languagesQuery = useContentLanguages();
  const languages = languagesQuery.data ?? [];
  // The tenant's content-language registry drives the locale — never a hard-wired business
  // default. Only when the registry itself is unreachable (and nothing is cached) does the page
  // fall back to the UI's own display language, purely so the catalogue isn't stuck loading
  // forever; `LocaleSwitcher` shows the load-error caption regardless (templates doc §13.10).
  const defaultLocale =
    languages.find((l) => l.defaultLocale)?.locale ??
    languages[0]?.locale ??
    (languagesQuery.isError ? i18n.language : undefined);
  const locale = searchParams.get('locale') || defaultLocale;

  const setLocale = (next: string) => {
    setSearchParams((params) => {
      params.set('locale', next);
      return params;
    });
  };

  const openKey = searchParams.get('key');
  const setOpenKey = (key: string | null) => {
    setSearchParams((params) => {
      if (key) params.set('key', key);
      else params.delete('key');
      return params;
    });
  };

  const filter = (searchParams.get('filter') as RowFilter | null) ?? 'all';
  const setFilter = (next: RowFilter) => {
    setSearchParams((params) => {
      params.set('filter', next);
      return params;
    });
  };
  const [search, setSearch] = useState('');
  const [copySeed, setCopySeed] = useState<{ key: string; text: string } | null>(null);
  const [deletingUnlisted, setDeletingUnlisted] = useState<ChatbotTemplateCatalogItem | null>(null);

  const catalogQuery = useChatbotTemplateCatalog(locale);
  const catalog = catalogQuery.data;
  const itemsByKey = useMemo(
    () => new Map((catalog?.items ?? []).map((item) => [item.contentKey, item])),
    [catalog],
  );
  const openItem: ChatbotTemplateCatalogItem | undefined = openKey
    ? itemsByKey.get(openKey)
    : undefined;

  // ── FAQ ────────────────────────────────────────────────────────────────────
  const faqQuery = useChatbotFaqIntents(locale);
  const putFaq = usePutChatbotFaqIntent(locale ?? '');
  const deleteFaq = useDeleteChatbotFaqIntent(locale ?? '');
  const putTemplate = usePutChatbotTemplate();
  const acceptDefault = useAcceptChatbotTemplateDefault();
  const resetTemplate = useResetChatbotTemplate();
  const [faqDialogIntent, setFaqDialogIntent] = useState<ChatbotFaqIntent | null | undefined>(
    undefined,
  );
  const [deletingFaq, setDeletingFaq] = useState<ChatbotFaqIntent | null>(null);
  const [deletingFaqAnswer, setDeletingFaqAnswer] = useState<ChatbotTemplateCatalogItem | null>(
    null,
  );
  const faqAnswerItems = (catalog?.items ?? []).filter((item) => item.family === 'FAQ_ANSWER');

  if (!canView) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
        <ErrorState
          title={t('settings:scaffold.accessRestrictedTitle')}
          message={t('settings:scaffold.accessRestrictedMessage')}
        />
      </Box>
    );
  }

  const groups = catalog
    ? catalog.groups
        .map((groupCode) => ({
          groupCode,
          items: catalog.items.filter(
            (item) =>
              item.group === groupCode &&
              matchesFilter(item, filter) &&
              (!search.trim() ||
                t(templateLabelKeys(item.contentKey).labelKey, item.contentKey)
                  .toLowerCase()
                  .includes(search.trim().toLowerCase()) ||
                item.contentKey.toLowerCase().includes(search.trim().toLowerCase()) ||
                previewTextOf(item).toLowerCase().includes(search.trim().toLowerCase())),
          ),
        }))
        .filter((group) => group.items.length > 0)
    : [];

  const missingRequired = (catalog?.items ?? []).filter(
    (item) => item.requirement === 'ALWAYS' && item.resolution === 'EMPTY',
  );
  const missingWhenAiOn = (catalog?.items ?? []).filter(
    (item) => item.requirement === 'WHEN_AI_ON' && item.resolution === 'EMPTY',
  );

  const localeLabel = languages.find((l) => l.locale === locale)?.name ?? locale ?? '';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Button
        component={RouterLink}
        to="/settings"
        size="small"
        color="inherit"
        startIcon={<ArrowBackIosNewRoundedIcon sx={{ fontSize: 14 }} />}
        sx={{ mb: 2, ml: -1, color: 'text.secondary' }}
      >
        {t('settings:scaffold.backToAllSettings')}
      </Button>

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <Box
          aria-hidden
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 44,
            height: 44,
            borderRadius: 2.5,
            color: '#fff',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
          }}
        >
          <ForumRoundedIcon />
        </Box>
        <Box>
          <Typography variant="h4" component="h1">
            {t('templates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('templates.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {canManage && !canPublish ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('templates.publishCapabilityRequired')}
        </Alert>
      ) : !canManage ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('settings:scaffold.readOnlyNotice')}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mt: 2.5, mb: 2 }}
      >
        <LocaleSwitcher
          languages={languages}
          value={locale ?? ''}
          onChange={setLocale}
          isLoading={languagesQuery.isLoading}
          isError={languagesQuery.isError}
          canViewLanguageSettings={hasPermission('catalog.structure.view')}
        />
        <TextField
          select
          size="small"
          label={t('templates.showFilterLabel')}
          value={filter}
          onChange={(event) => setFilter(event.target.value as RowFilter)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">{t('templates.showFilterOptions.all')}</MenuItem>
          <MenuItem value="attention">{t('templates.showFilterOptions.attention')}</MenuItem>
          <MenuItem value="yours">{t('templates.showFilterOptions.yours')}</MenuItem>
          <MenuItem value="default">{t('templates.showFilterOptions.default')}</MenuItem>
          <MenuItem value="missing">{t('templates.showFilterOptions.missing')}</MenuItem>
          <MenuItem value="variants">{t('templates.showFilterOptions.variants')}</MenuItem>
        </TextField>
        <TextField
          size="small"
          placeholder={t('templates.findPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
              ),
            },
          }}
          sx={{ minWidth: 220 }}
        />
      </Stack>

      {(missingRequired.length > 0 || missingWhenAiOn.length > 0) && filter === 'all' ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={() => setFilter('attention')}>
              {t('templates.missing.show')}
            </Button>
          }
        >
          <Stack>
            {missingRequired.length > 0 ? (
              <span>
                {t(
                  missingRequired.length === 1
                    ? 'templates.missing.banner_one'
                    : 'templates.missing.banner_other',
                  { count: missingRequired.length, language: localeLabel },
                )}
              </span>
            ) : null}
            {missingWhenAiOn.length > 0 ? (
              <span>
                {t(
                  missingWhenAiOn.length === 1
                    ? 'templates.missing.whenAiOn_one'
                    : 'templates.missing.whenAiOn_other',
                  { count: missingWhenAiOn.length },
                )}
              </span>
            ) : null}
          </Stack>
        </Alert>
      ) : null}

      {!locale || catalogQuery.isLoading ? (
        <LoadingState label={t('templates.loading')} />
      ) : catalogQuery.isError ? (
        <ErrorState
          title={t('templates.loadErrorTitle')}
          onRetry={() => void catalogQuery.refetch()}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          title={t('templates.noMatches')}
          description=""
          action={
            filter !== 'all' || search ? (
              <Button
                onClick={() => {
                  setFilter('all');
                  setSearch('');
                }}
              >
                {t('templates.clearFilter')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Stack spacing={2.5}>
          {groups.map((group) => (
            <Box key={group.groupCode}>
              <Typography variant="overline" color="text.secondary">
                {t(templateGroupLabelKey(group.groupCode), group.groupCode)}
              </Typography>
              <Stack
                divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
              >
                {group.items.map((item) => {
                  const { labelKey } = templateLabelKeys(item.contentKey);
                  const label =
                    item.family === 'FAQ_ANSWER'
                      ? item.referencedByIntents.length > 0
                        ? t('templates.faqAnswer.label', {
                            intents: item.referencedByIntents.join(', '),
                          })
                        : item.contentKey
                      : t(labelKey, item.contentKey);
                  const chipMeta = STATUS_CHIP[item.resolution];
                  const preview =
                    item.resolution === 'EMPTY' ? t('templates.nothingPreview') : previewTextOf(item);
                  const rowIssues = item.issues.filter((issue) =>
                    KNOWN_ISSUE_CODES.has(issue.code),
                  );
                  const needsAttention = rowIssues.length > 0;

                  return (
                    <Stack
                      key={item.contentKey}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ px: 2, py: 1.5, minHeight: 52, flexWrap: 'wrap' }}
                    >
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          {item.requirement === 'ALWAYS' && item.resolution === 'EMPTY' ? (
                            <Tooltip title={t('templates.requirementTooltip.ALWAYS')}>
                              <WarningAmberRoundedIcon fontSize="small" color="warning" />
                            </Tooltip>
                          ) : null}
                          <Typography variant="subtitle2" noWrap>
                            {label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {item.contentKey}
                          </Typography>
                          {item.requirement !== 'OPTIONAL' ? (
                            <Tooltip
                              title={t(
                                item.requirement === 'ALWAYS'
                                  ? 'templates.requirementTooltip.ALWAYS'
                                  : 'templates.requirementTooltip.WHEN_AI_ON',
                              )}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(
                                  item.requirement === 'ALWAYS'
                                    ? 'templates.requirement.ALWAYS'
                                    : 'templates.requirement.WHEN_AI_ON',
                                )}
                              />
                            </Tooltip>
                          ) : null}
                          {item.channelVariants.map((variantChannel) => (
                            <Tooltip
                              key={variantChannel}
                              title={t('templates.variant.tooltip', {
                                channel: variantChannel,
                                language: localeLabel,
                              })}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={variantChannel === 'TELEGRAM' ? 'Telegram' : 'MAX'}
                                onClick={() => setOpenKey(item.contentKey)}
                              />
                            </Tooltip>
                          ))}
                          {item.family === 'FAQ_ANSWER' &&
                          item.issues.some((issue) => issue.code === 'LEGACY_FAQ_ANSWER_KEY') ? (
                            <Tooltip title={t('templates.issues.LEGACY_FAQ_ANSWER_KEY')}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t('templates.faqAnswer.legacy')}
                              />
                            </Tooltip>
                          ) : null}
                          {needsAttention ? (
                            <Tooltip title={rowIssues.map((i) => issueSentence(i, t)).join(' ')}>
                              <Chip
                                size="small"
                                variant="outlined"
                                color="warning"
                                icon={<ReportProblemRoundedIcon fontSize="small" />}
                                label={t('templates.attention')}
                                aria-label={t('templates.attentionAria', {
                                  label,
                                  issues: rowIssues.map((i) => issueSentence(i, t)).join(' '),
                                })}
                              />
                            </Tooltip>
                          ) : null}
                        </Stack>
                        <Typography
                          variant="body2"
                          color={
                            item.resolution === 'ACCEPTED_DEFAULT' ||
                            item.resolution === 'SYSTEM_DEFAULT'
                              ? 'text.secondary'
                              : 'text.primary'
                          }
                          noWrap
                          sx={
                            item.resolution === 'EMPTY'
                              ? { fontStyle: 'italic', color: 'text.disabled' }
                              : undefined
                          }
                        >
                          {preview}
                        </Typography>
                        {item.family === 'FAQ_ANSWER' ? (
                          <Typography variant="caption" color="text.secondary">
                            {item.referencedByIntents.length > 0
                              ? `${t('templates.faqAnswer.usedBy')} ${item.referencedByIntents.join(', ')}`
                              : t('templates.faqAnswer.unused')}
                          </Typography>
                        ) : item.resolution === 'SYSTEM_DEFAULT' ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('templates.resolutionCaption.SYSTEM_DEFAULT')}
                          </Typography>
                        ) : item.resolution === 'EMPTY' ? (
                          <Typography variant="caption" color="text.secondary">
                            {t('templates.resolutionCaption.EMPTY')}
                          </Typography>
                        ) : null}
                      </Box>

                      {item.family === 'UNLISTED' ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<BlockRoundedIcon fontSize="small" />}
                          label={t('templates.unlistedChip')}
                        />
                      ) : (
                        <Tooltip
                          title={
                            item.resolution === 'ACCEPTED_DEFAULT'
                              ? t('templates.resolutionTooltip.ACCEPTED_DEFAULT')
                              : item.resolution === 'SYSTEM_DEFAULT'
                                ? t('templates.resolutionTooltip.SYSTEM_DEFAULT')
                                : ''
                          }
                        >
                          <Chip
                            size="small"
                            variant={chipMeta.variant}
                            color={chipMeta.color}
                            icon={
                              chipMeta.icon === 'verified' ? (
                                <VerifiedRoundedIcon fontSize="small" />
                              ) : undefined
                            }
                            label={t(`templates.resolution.${item.resolution}`)}
                          />
                        </Tooltip>
                      )}

                      {item.family === 'UNLISTED' ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenKey(item.contentKey)}
                          >
                            {t('templates.view')}
                          </Button>
                          {canPublish ? (
                            <>
                              {(() => {
                                const replacementKey = item.issues.find(
                                  (issue) => issue.code === 'KEY_RETIRED' && issue.detail,
                                )?.detail;
                                if (!replacementKey) return null;
                                return (
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      setCopySeed({
                                        key: replacementKey,
                                        text:
                                          item.template.parts.find((p) => p.type === 'TEXT')
                                            ?.text ?? '',
                                      })
                                    }
                                  >
                                    {t('templates.unlisted.copyTo', {
                                      label: t(
                                        templateLabelKeys(replacementKey).labelKey,
                                        replacementKey,
                                      ),
                                    })}
                                  </Button>
                                );
                              })()}
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setDeletingUnlisted(item)}
                              >
                                {t('templates.unlisted.delete')}
                              </Button>
                            </>
                          ) : null}
                        </Stack>
                      ) : item.resolution === 'EMPTY' ? (
                        <Stack direction="row" spacing={1}>
                          {item.template.hasDefault && canPublish ? (
                            <Button
                              size="small"
                              onClick={() => acceptDefault.mutate(item.contentKey)}
                            >
                              {t('templates.usePlatformDefault')}
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenKey(item.contentKey)}
                          >
                            {t('templates.writeText')}
                          </Button>
                        </Stack>
                      ) : item.resolution === 'SYSTEM_DEFAULT' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setOpenKey(item.contentKey)}
                        >
                          {t('templates.customise')}
                        </Button>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setOpenKey(item.contentKey)}
                          >
                            {canPublish ? t('templates.customise') : t('templates.view', 'View')}
                          </Button>
                          {item.family === 'FAQ_ANSWER' && canPublish ? (
                            <Tooltip
                              title={
                                item.referencedByIntents.length > 0
                                  ? t('templates.faqAnswer.deleteBlocked', {
                                      intents: item.referencedByIntents.join(', '),
                                    })
                                  : t('templates.faqAnswer.deleteText')
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={item.referencedByIntents.length > 0}
                                  onClick={() => setDeletingFaqAnswer(item)}
                                  aria-label={t('templates.faqAnswer.deleteText')}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ))}

          {/* ── FAQ questions ──────────────────────────────────────────────────── */}
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <LiveHelpRoundedIcon color="action" />
                <Typography variant="h6">{t('faq.title')}</Typography>
              </Stack>
              {canPublish ? (
                <Button size="small" variant="outlined" onClick={() => setFaqDialogIntent(null)}>
                  {t('faq.addEntry')}
                </Button>
              ) : null}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('faq.description')}
            </Typography>
            {faqQuery.isLoading ? (
              <LoadingState fullHeight={false} label={t('faq.loading')} />
            ) : faqQuery.isError ? (
              <Alert
                severity="error"
                action={
                  <Button size="small" onClick={() => void faqQuery.refetch()}>
                    {t('templates.retry', 'Retry')}
                  </Button>
                }
              >
                {t('faq.loadError')}
              </Alert>
            ) : (faqQuery.data?.items.length ?? 0) === 0 ? (
              <EmptyState title={t('faq.emptyTitle')} description={t('faq.emptyDescription')} />
            ) : (
              <Stack
                divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
              >
                {faqQuery.data!.items.map((intent) => {
                  const answerItem = itemsByKey.get(intent.answerContentKey);
                  const answerMissing = !answerItem || answerItem.resolution === 'EMPTY';
                  return (
                    <Stack
                      key={intent.intentCode}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ px: 2, py: 1.5, flexWrap: 'wrap' }}
                    >
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 140 }}>
                        {intent.intentCode}
                      </Typography>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {intent.utterances[0]}
                          {intent.utterances.length > 1
                            ? t('faq.moreUtterances', { count: intent.utterances.length - 1 })
                            : ''}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('faq.answerKey', { key: intent.answerContentKey })}
                            onClick={() => setOpenKey(intent.answerContentKey)}
                            sx={{ fontFamily: 'monospace' }}
                          />
                          {answerMissing ? (
                            <Typography variant="caption" color="warning.main">
                              {t('faq.answerMissing', { language: localeLabel })}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                      <Switch
                        checked={intent.enabled}
                        disabled={!canPublish}
                        onChange={(event) =>
                          putFaq.mutate({
                            intentCode: intent.intentCode,
                            body: {
                              locale,
                              utterances: intent.utterances,
                              keywords: intent.keywords ?? [],
                              answerContentKey: intent.answerContentKey,
                              enabled: event.target.checked,
                            },
                          })
                        }
                        slotProps={{
                          input: { 'aria-label': t('faq.toggleAria', { code: intent.intentCode }) },
                        }}
                      />
                      {canPublish ? (
                        <>
                          <Tooltip title={t('faq.editAria', { code: intent.intentCode })}>
                            <IconButton size="small" onClick={() => setFaqDialogIntent(intent)}>
                              <EditRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('faq.deleteAria', { code: intent.intentCode })}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeletingFaq(intent)}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      {openItem && locale && catalog ? (
        <TemplateEditorDrawer
          open={Boolean(openItem)}
          onClose={() => setOpenKey(null)}
          item={openItem}
          catalog={catalog}
          locale={locale}
          canPublish={canPublish}
        />
      ) : copySeed && catalog ? (
        <TemplateEditorDrawer
          open
          onClose={() => setCopySeed(null)}
          item={
            itemsByKey.get(copySeed.key) ?? {
              contentKey: copySeed.key,
              kind: 'BOT_REPLY',
              family: 'FIXED',
              group: 'system',
              allowedPlaceholders: [],
              requirement: 'OPTIONAL',
              renderMode: 'SINGLE',
              actionMarkup: 'ANY',
              hubActions: [],
              expectedActionTypes: [],
              renderedTogetherWith: [],
              systemDefault: false,
              resolution: 'EMPTY',
              channelVariants: [],
              referencedByIntents: [],
              issues: [],
              template: {
                contentKey: copySeed.key,
                kind: 'BOT_REPLY',
                locale: locale ?? '',
                customized: false,
                hasDefault: false,
                acceptedDefault: false,
                parts: [],
                actions: [],
                defaultParts: [],
                defaultActions: [],
                effectiveParts: [],
                effectiveActions: [],
                version: null,
              },
            }
          }
          catalog={catalog}
          locale={locale ?? ''}
          canPublish={canPublish}
          seedText={copySeed.text}
        />
      ) : null}

      <FaqEntryDialog
        open={faqDialogIntent !== undefined}
        onClose={() => setFaqDialogIntent(undefined)}
        intent={faqDialogIntent ?? null}
        faqAnswerItems={faqAnswerItems}
        faqAnswerKeyPattern={catalog?.faqAnswerKeyPattern ?? '^bot\\.faq\\.[a-z0-9][a-z0-9._-]*$'}
        faqAnswerPlaceholders={(catalog?.faqAnswerPlaceholders ?? []).map(
          (name) =>
            catalog?.placeholders.find((p) => p.name === name) ?? {
              name,
              valueLanguage: 'NEUTRAL' as const,
            },
        )}
        disabled={!canPublish}
        saving={putFaq.isPending}
        onSave={async (body) => {
          await putFaq.mutateAsync({ intentCode: body.intentCode, body: { locale, ...body } });
          setFaqDialogIntent(undefined);
        }}
        onSaveNewAnswerText={async (key, text) => {
          await putTemplate.mutateAsync({
            key,
            scope: { locale: locale ?? '' },
            body: { parts: [{ type: 'TEXT', text, format: 'PLAIN' }] },
          });
        }}
      />

      <Dialog open={Boolean(deletingFaq)} onClose={() => setDeletingFaq(null)}>
        <DialogTitle>{t('faq.deleteConfirmTitle', { code: deletingFaq?.intentCode })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('faq.deleteConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFaq(null)}>{t('editor.cancel')}</Button>
          <Button
            color="error"
            onClick={() => {
              if (!deletingFaq) return;
              deleteFaq.mutate(deletingFaq.intentCode, { onSuccess: () => setDeletingFaq(null) });
            }}
          >
            {t('faq.deleteConfirmAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingFaqAnswer)} onClose={() => setDeletingFaqAnswer(null)}>
        <DialogTitle>
          {t('templates.faqAnswer.deleteDialog.title', {
            key: deletingFaqAnswer?.contentKey,
            language: localeLabel,
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t('templates.faqAnswer.deleteDialog.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingFaqAnswer(null)} autoFocus>
            {t('editor.cancel')}
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (!deletingFaqAnswer) return;
              resetTemplate.mutate(
                { key: deletingFaqAnswer.contentKey, scope: { locale: locale ?? '' } },
                { onSuccess: () => setDeletingFaqAnswer(null) },
              );
            }}
          >
            {t('templates.unlisted.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingUnlisted)} onClose={() => setDeletingUnlisted(null)}>
        <DialogTitle>
          {t('templates.unlisted.deleteDialog.title', { key: deletingUnlisted?.contentKey })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('templates.unlisted.deleteDialog.body')}
            {deletingUnlisted && deletingUnlisted.channelVariants.length > 0
              ? ` ${t('templates.unlisted.deleteDialog.bodyVariants')}`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingUnlisted(null)} autoFocus>
            {t('editor.cancel')}
          </Button>
          <Button
            color="error"
            onClick={() => {
              if (!deletingUnlisted) return;
              resetTemplate.mutate(
                {
                  key: deletingUnlisted.contentKey,
                  scope: { locale: locale ?? '' },
                  cascade: deletingUnlisted.channelVariants.length > 0,
                },
                { onSuccess: () => setDeletingUnlisted(null) },
              );
            }}
          >
            {t('templates.unlisted.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
